import { minBy } from 'es-toolkit';
import React, { useEffect, useMemo } from 'react';

import { Direction2D, distanceEquals, LineSegment2D, Point2D } from '@paragon/geometry-library';
import { BearingVisualData } from '@paragon/three-shared';
import { min, notNullish, roundToIncrement } from '@paragon/utilities';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { insertBearing, insertionBearingHeight, insertionBearingWidth } from '../../store/actions/insertActions';
import { addInsertionProfileBearing } from '../../store/actions/profileActions';
import {
  BearingInsertion,
  BearingInsertionInfo,
  getBearingInsertionInfo,
} from '../../store/selectors/bearingInsertionSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { SnappingLevel } from '../../store/slices/applicationSlice';
import { BearingDimensions } from '../dimension/BearingDimensions';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { BearingBaseVisual } from './BearingVisual';

export function BearingInsertionVisual() {
  const isInsertingBearing = useAppSelector(getBearingInsertionInfo);

  return isInsertingBearing ? <BearingInsertionVisualInternals /> : null;
}

function BearingInsertionVisualInternals() {
  const dispatch = useAppDispatch();
  const componentDesign = useAppSelector(getActiveComponentDesign);
  const insertionData = useAppSelector(getBearingInsertionInfo);
  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  const mousePosition = useMousePositionInScene();

  const insertionPositionData = useMemo(
    () =>
      insertionData && insertionData.snaps.length !== 0 && mousePosition
        ? snapMouseToBearingInsertionSnaps(Point2D.fromObject(mousePosition), insertionData)
        : null,
    [insertionData, mousePosition],
  );

  useEffect(() => {
    const insert = () => {
      if (insertionPositionData) {
        if (insertingObjectInfo?.type === 'bearing') {
          dispatch(insertBearing(insertionPositionData));
        } else if (insertingObjectInfo?.type === 'componentDesign') {
          dispatch(addInsertionProfileBearing(insertionPositionData));
        } else {
          throw new Error('Expected to be inserting a bearing');
        }
      }
    };

    threeJsDom.addGlobalListener('click', insert);
    return () => threeJsDom.removeGlobalListener('click', insert);
  }, [dispatch, insertingObjectInfo?.type, insertionPositionData]);

  if (!componentDesign || !insertionPositionData) return null;

  const { bearingDefinitionPoint, justification, trussSide } = insertionPositionData;

  let bearingMidpoint = bearingDefinitionPoint;
  if (justification !== 'Center') {
    const justificationDirection =
      trussSide === 'Bottom'
        ? justification === 'Port'
          ? Direction2D.right
          : Direction2D.left
        : (trussSide === 'Left') === (justification === 'Port')
          ? Direction2D.down
          : Direction2D.up;
    const translation = justificationDirection.toVector(insertionBearingWidth / 2);
    bearingMidpoint = bearingDefinitionPoint.translate(translation);
  }

  const visualData: BearingVisualData = {
    width: insertionBearingWidth,
    height: insertionBearingHeight,
    normal: trussSide === 'Bottom' ? Direction2D.up : trussSide === 'Left' ? Direction2D.right : Direction2D.left,
    position: bearingMidpoint,
  };

  return (
    <>
      <BearingBaseVisual
        bearingType={trussSide === 'Bottom' ? 'DoublePlate' : 'ToeNail'}
        component={componentDesign.component}
        depth={componentDesign.component.members[0]?.thickness ?? 1.5}
        isContinuous={false}
        visualData={visualData}
      />
      <BearingDimensions visualData={visualData} />
    </>
  );
}

function snapMouseToBearingInsertionSnaps(mousePosition: Point2D, snapInfo: BearingInsertionInfo): BearingInsertion {
  // Find closest line segment that is snapped to
  const closestLineSegment = minBy(
    snapInfo.snaps
      .filter((snap): snap is { type: 'segment' } & typeof snap => snap.type === 'segment')
      .map((snap) => {
        const segment = snap.item;
        const mouseProjection = snapMousePositionToSnapGradient(
          segment,
          mousePosition.projectOntoLine(segment.toLine()),
          snapInfo.snappingDistance,
        );
        return mouseProjection && segment.contains(mouseProjection)
          ? { snap, mouseProjection, distance: mouseProjection.distanceTo(mousePosition) }
          : null;
      })
      .filter(notNullish),
    ({ distance }) => distance,
  );
  // Find closest point. If multiple points coincide, pick between vertical and horizontal bearings based on mouse position.
  const allPointSnaps = snapInfo.snaps
    .filter((snap): snap is { type: 'point' } & typeof snap => snap.type === 'point')
    .map((snap) => ({ snap, mouseProjection: snap.item, distance: snap.item.distanceTo(mousePosition) }));
  const closestDistance = min(allPointSnaps.map(({ distance }) => distance)) ?? 0;
  const closestPoints = allPointSnaps.filter(({ distance }) => distanceEquals(distance, closestDistance));
  let closestPoint = closestPoints[0] as (typeof closestPoints)[0] | undefined;
  if (closestPoints.length > 1) {
    const directionFromMouseToPointIsMoreHorizontalThanVertical =
      Math.abs(Direction2D.fromPoints(mousePosition, closestPoint!.snap.item).dotProduct(Direction2D.right)) >
      Math.SQRT1_2;

    closestPoint =
      closestPoints.find(
        (point) => (point.snap.trussSide !== 'Bottom') === directionFromMouseToPointIsMoreHorizontalThanVertical,
      ) ?? closestPoint;
  }

  // Find the closer of the two snaps.
  const pointPreference = 1.2;
  const closestItem =
    !closestLineSegment || !closestPoint
      ? (closestLineSegment ?? closestPoint ?? null)
      : closestLineSegment.distance * pointPreference < closestPoint.distance
        ? closestLineSegment
        : closestPoint;

  if (!closestItem) throw new Error('Expected to find at least one place to snap the insertion bearing');

  const {
    mouseProjection,
    snap: { referenceLineGuid, trussSide, justification, chordType },
  } = closestItem;

  return { bearingDefinitionPoint: mouseProjection, justification, trussSide, chordType, referenceLineGuid };
}

function snapMousePositionToSnapGradient(
  segment: LineSegment2D,
  mousePosition: Point2D,
  snapLevel: SnappingLevel,
): Point2D | null {
  const { x, y } = mousePosition;
  const increment = snapLevel === 'coarse' ? 12 : snapLevel === 'fine' ? 0.25 : 1;
  const [newX, newY] = [roundToIncrement(x, increment), roundToIncrement(y, increment)];
  return new Point2D(newX, newY).projectOntoLineSegment(segment);
}
